import React from "react";
import { Formik, Form } from "formik";
import BottomModal from "./BottomModal";
import Input from "./Input";
import Label from "./Label";
import { ChatbotWidgetInitResponse } from "../../services/chatbotWidget.service";

interface AddressData {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  notes: string;
}

interface AddressFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (address: string) => void;
  prompt?: string;
  disabled?: boolean;
  config?: ChatbotWidgetInitResponse;
}

const initialValues: AddressData = {
  street: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  notes: "",
};

const AddressFormModal: React.FC<AddressFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  disabled = false,
  config,
}) => {
  // Validation function
  const validate = (values: AddressData) => {
    const errors: Partial<AddressData> = {};

    if (!values.street.trim()) {
      errors.street = "Street address is required";
    }
    if (!values.city.trim()) {
      errors.city = "City is required";
    }
    if (!values.state.trim()) {
      errors.state = "State/Province is required";
    }
    if (!values.postalCode.trim()) {
      errors.postalCode = "Postal code is required";
    }
    if (!values.country.trim()) {
      errors.country = "Country is required";
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = (values: AddressData, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
    // Format address as a single string
    const addressParts = [
      values.street,
      values.city,
      values.state,
      values.postalCode,
      values.country,
    ];

    if (values.notes.trim()) {
      addressParts.push(`(${values.notes})`);
    }

    const formattedAddress = addressParts.join(", ");
    onSubmit(formattedAddress);
    setSubmitting(false);
  };

  return (
    <BottomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Provide Address"
      sections={[]}
      selectedItem={null}
      onItemSelect={() => { }}
      showSearch={false}
      containerPositioned={true}
      customContent={
        <Formik
          initialValues={initialValues}
          validate={validate}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({  values, handleChange, handleBlur, isSubmitting }) => (
            <Form className="space-y-4">
              <div className="px-4 py-4 space-y-4">

                <div className="space-y-4">
                  {/* Street Address */}
                  <div>
                    <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address<span className="text-red-500 ml-1">*</span>
                    </label>
                    <Input
                      name="street"
                      type="text"
                      placeholder="123 Main St, Apt 4B"
                      disabled={disabled || isSubmitting}
                    />
                  </div>

                  {/* City */}
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                      City<span className="text-red-500 ml-1">*</span>
                    </label>
                    <Input
                      name="city"
                      type="text"
                      placeholder="New York"
                      disabled={disabled || isSubmitting}
                    />
                  </div>

                  {/* State & Postal Code */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                        State/Province<span className="text-red-500 ml-1">*</span>
                      </label>
                      <Input
                        name="state"
                        type="text"
                        placeholder="NY"
                        disabled={disabled || isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                        Postal Code<span className="text-red-500 ml-1">*</span>
                      </label>
                      <Input
                        name="postalCode"
                        type="text"
                        placeholder="10001"
                        disabled={disabled || isSubmitting}
                      />
                    </div>
                  </div>

                  {/* Country */}
                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                      Country<span className="text-red-500 ml-1">*</span>
                    </label>
                    <Input
                      name="country"
                      type="text"
                      placeholder="United States"
                      disabled={disabled || isSubmitting}
                    />
                  </div>

                  {/* Additional Notes (Optional) */}
                  <div>
                    <Label htmlFor="notes">Additional Notes (Optional)</Label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={values.notes}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Delivery instructions, apartment number, etc."
                      rows={2}
                      disabled={disabled || isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm disabled:bg-gray-100"
                    />
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      }
      footerButtons={[
        {
          label: 'Cancel',
          onClick: onClose,
          variant: 'secondary',
          className: "flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
        },
        {
          label: 'Submit Address',
          onClick: () => {
            // Find and submit the form
            const form = document.querySelector('form');
            if (form) {
              form.requestSubmit();
            }
          },
          variant: 'primary',
          style: {
            backgroundColor: config?.primaryColor
          },
          className: "flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium border-none"
        }
      ]}
    />
  );
};

export default AddressFormModal;
